/**
 * The IndexedDB schema.
 *
 * WHY DEXIE AND NOT SQLITE (ADR 0001)
 * ---------------------------------
 * 1.6.2 needs `better-sqlite3` plus `electron-rebuild` on every Electron
 * upgrade, with `electronVersion` pinned in package.json. 2.0 must run as a
 * PWA, where a native module cannot load at all. Dropping it removes a
 * recurring build-breakage source and makes one codebase serve both shells.
 *
 * The honest trade-off: IndexedDB transactions are asynchronous and
 * browser-managed, so multi-step business operations must be written in
 * exactly one place. That discipline is `data/services/*.ts`, and an ESLint
 * rule bans direct Dexie use outside `data/`.
 *
 * WHY ONE `documents` TABLE
 * ------------------------
 * Eleven tables would mean eleven calc paths, eleven renderers, eleven
 * repositories and eleven conversion routines. One discriminated table means
 * the calculation engine and the document renderer each exist once.
 *
 * WHY `&` ON A THREE-PART COMPOUND
 * -------------------------------
 * The real 1.6.2 export proves all three parts are required:
 *   - businessId     both companies use the SAME "26-27/" series
 *   - financialYear  company_1 has both "25-26" (343) and "26-27" (129)
 *   - docType        invoices, quotations and POs number independently
 * A global unique index would reject valid data; a two-part one would too.
 */
import Dexie, { type Table } from 'dexie';
import type {
  Attachment,
  AuditLog,
  Business,
  Category,
  Document,
  DocumentLine,
  DocumentLink,
  DocumentSequence,
  Expense,
  Party,
  Payment,
  PaymentAllocation,
  PriceList,
  PriceListItem,
  Product,
  Setting,
  StockLocation,
  StockTransaction,
  TaxRate,
  UserAccount,
  SyncOutboxEntry,
  SyncStateEntry,
  DeviceRecord,
  SyncConflict,
} from './types';
import { runMigrations } from './migrations';

/** Every table, in one list, so a transaction can name all of them. */
export const TABLE_NAMES = [
  'businesses',
  'parties',
  'categories',
  'taxRates',
  'products',
  'stockLocations',
  'documents',
  'documentLines',
  'documentLinks',
  'payments',
  'paymentAllocations',
  'expenses',
  'stockTransactions',
  'documentSequences',
  'settings',
  'auditLogs',
  'attachments',
  'userAccounts',
  'priceLists',
  'priceListItems',
  'syncOutbox',
  'syncState',
  'devices',
  'syncConflicts',
] as const;

export class DNRDatabase extends Dexie {
  businesses!: Table<Business, string>;
  parties!: Table<Party, string>;
  categories!: Table<Category, string>;
  taxRates!: Table<TaxRate, string>;
  products!: Table<Product, string>;
  stockLocations!: Table<StockLocation, string>;
  documents!: Table<Document, string>;
  documentLines!: Table<DocumentLine, string>;
  documentLinks!: Table<DocumentLink, string>;
  payments!: Table<Payment, string>;
  paymentAllocations!: Table<PaymentAllocation, string>;
  expenses!: Table<Expense, string>;
  stockTransactions!: Table<StockTransaction, string>;
  documentSequences!: Table<DocumentSequence, string>;
  settings!: Table<Setting, string>;
  auditLogs!: Table<AuditLog, string>;
  attachments!: Table<Attachment, string>;
  userAccounts!: Table<UserAccount, string>;
  priceLists!: Table<PriceList, string>;
  priceListItems!: Table<PriceListItem, string>;
  /** v2. Pending mutations. See the v2 schema comment. */
  syncOutbox!: Table<SyncOutboxEntry, string>;
  syncState!: Table<SyncStateEntry, string>;
  devices!: Table<DeviceRecord, string>;
  syncConflicts!: Table<SyncConflict, string>;

  constructor(name = 'dnr-vyapar') {
    super(name);

    /**
     * v1 — initial schema.
     *
     * Index notes:
     *  - `&` marks a UNIQUE index. The documents one is the numbering
     *    invariant: a duplicate GST invoice number becomes physically
     *    impossible, not merely discouraged by a UI confirmation dialog.
     *  - compound `[a+b]` indexes match the list queries, so a filtered
     *    list never full-scans.
     */
    this.version(1).stores({
      businesses: 'id, legalName',
      parties:
        'id, businessId, kind, code, name, gstin, stateCode, isActive, ' +
        '[businessId+kind], [businessId+isActive], [businessId+name]',
      categories: 'id, businessId, parentId, kind, [businessId+kind]',
      taxRates: 'id, businessId, rateBp, isActive, [businessId+isActive]',
      products:
        'id, businessId, sku, name, categoryId, taxRateId, kind, isActive, ' +
        '[businessId+isActive], [businessId+categoryId], [businessId+name]',
      stockLocations: 'id, businessId, name, isDefault',

      documents:
        'id, businessId, docType, docNumber, docDate, status, ' +
        'customerId, supplierId, linkedParentDocId, originalDocId, ' +
        'salesOrderId, purchaseOrderId, ' +
        '[businessId+docType], [businessId+docType+docDate], ' +
        '[businessId+docType+status], [customerId+docDate], [supplierId+docDate], ' +
        // ★ the numbering invariant
        '&[businessId+docType+financialYear+docNumber]',

      documentLines:
        'id, documentId, productId, businessId, ' + '[documentId+lineNo], [documentId+productId]',

      documentLinks:
        'id, fromDocumentId, fromLineId, toDocumentId, toLineId, ' +
        '[toDocumentId], [fromDocumentId], [toDocumentId+toLineId], ' +
        '[fromDocumentId+fromLineId]',

      payments:
        'id, businessId, paymentNumber, paymentDate, customerId, supplierId, ' +
        'direction, status, [businessId+paymentDate], [customerId+paymentDate], ' +
        '[supplierId+paymentDate]',
      paymentAllocations:
        'id, businessId, paymentId, documentId, customerId, supplierId, ' +
        '[paymentId+documentId], [documentId+paymentId]',
      expenses:
        'id, businessId, expenseNumber, expenseDate, category, paymentId, ' +
        'customerId, [businessId+expenseDate], [businessId+category]',

      stockTransactions:
        'id, businessId, productId, stockLocationId, documentId, docDate, ' +
        'transferGroupId, [productId+docDate], [stockLocationId+productId], ' +
        '[businessId+docDate], [documentId+productId]',

      documentSequences: 'id, businessId, docType, financialYear, [businessId+docType]',
      settings: 'id, scope, scopeId, businessId, [scope+key], [scope+scopeId+key]',
      auditLogs:
        'id, businessId, entityType, entityId, action, occurredAt, ' +
        '[entityType+entityId], [businessId+occurredAt], [entityId+entityType]',
      attachments: 'id, businessId, kind, createdAt',
      userAccounts: 'id, businessId, &username, isActive',
      priceLists: 'id, businessId, isDefault',
      priceListItems: 'id, priceListId, productId, [priceListId+productId]',
    });

    /**
     * v2 — cloud sync foundation. ADDITIVE ONLY.
     *
     * Nothing is dropped, renamed or re-indexed, so an existing business's
     * invoices, payments and stock ledger migrate across untouched. That is a
     * requirement, not luck: a schema change that rewrote a ledger would break
     * exactly the records users cannot afford to lose.
     *
     * These four tables are the LOCAL side of sync. They hold no business data —
     * Dexie already holds that — and creating a second copy of the database
     * would give two sources of truth, which is the failure ADR 0009 exists to
     * prevent. They hold only what the database cannot tell us:
     *
     *  syncOutbox    mutations not yet accepted by a provider
     *  syncState     per business + device + provider position
     *  devices       the persistent device identity
     *  syncConflicts changes that need a person
     *
     * ALL of this is optional. With no provider configured these tables stay
     * empty and nothing in the application changes behaviour — the app is not
     * aware of them except through the sync service.
     */
    this.version(2).stores({
      /**
       * Pending mutations. Indexed by `[businessId+status]` because the engine's
       * only query is "next batch of pending work for THIS business" — a full
       * scan here would be the one hot path a large business feels.
       */
      syncOutbox:
        'changeId, businessId, deviceId, entityType, entityId, status, ' +
        'createdAt, [businessId+status], [businessId+entityType+entityId], [entityType+entityId]',
      /**
       * One row per (business, device, provider). The composite primary index is
       * the identity: a device that switches provider gets a NEW row with a
       * fresh cursor, because the two providers' change logs are unrelated and
       * interleaving their cursors would either skip changes or replay them.
       */
      syncState: 'id, businessId, deviceId, providerId, [businessId+deviceId+providerId]',
      /** The persistent device identity. See deviceIdentity.ts. */
      devices: 'id, businessId, [businessId+lastSeenAt]',
      /** Awaiting a person. Resolved rows are kept for the audit trail. */
      syncConflicts:
        'conflictId, businessId, entityType, entityId, resolved, detectedAt, ' +
        '[businessId+resolved], [businessId+entityType+entityId]',
    });
  }
}

let singleton: DNRDatabase | null = null;

/** The app-wide instance. Tests pass their own name for isolation. */
export function getDb(name?: string): DNRDatabase {
  if (name) return new DNRDatabase(name);
  singleton ??= new DNRDatabase();
  return singleton;
}

/** Delete and recreate. Test-only: never call this in app code. */
export async function resetDb(name = 'dnr-vyapar-test'): Promise<DNRDatabase> {
  const existing = new DNRDatabase(name);
  await existing.delete();
  return new DNRDatabase(name);
}

/** Open the singleton database and run pending data-version migrations.
 * Called once at app startup — before any query reads business data — so that
 * legacy entity rows receive their `syncChangeId` backfill and sequence tables
 * are re-derived before the first sync or restore runs.
 */
export async function ensureDbReady(name?: string): Promise<DNRDatabase> {
  const db = getDb(name);
  await db.open();
  await runMigrations(db);
  return db;
}

export type TableName = (typeof TABLE_NAMES)[number];
